import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import breadcrumbs from 'images/breadcrumbs.svg';
import TooltipForElement from 'components/Tooltips/TooltipForElement';
import { CustomPop } from 'components/Popover/CustomPop';
import classNames from 'classnames';
import style from './DataController.module.scss';

type TSize = 'smallBtn' | 'bigBtn';
interface IDataController {
	onClickInfo?: () => void;
	onClickArchive?: () => void;
	onClickRestore?: () => void;
	onClickDelete?: () => void;
	onClickEdit?: () => void; // You should choose between two types of edit handlers:
	URLWithIDToEdit?: string; // by navigate to edit page URL or by callback
	URLWithIDToInfo?: string;
	size: TSize;
	isOpenInfo?: boolean;
	customEditButton?: React.ReactNode;
}

const DataController: React.FC<IDataController> = ({
	onClickInfo,
	onClickArchive,
	onClickRestore,
	onClickDelete,
	onClickEdit,
	URLWithIDToEdit,
	URLWithIDToInfo,
	size,
	isOpenInfo = false,
	customEditButton,
}) => {
	const { t } = useTranslation('listing');

	const btnClasses = classNames(style.btn, {
		[style.smallBtn]: size === 'smallBtn',
		[style.bigBtn]: size === 'bigBtn',
	});
	const infoIconClasses = classNames(style.icon, {
		[style.hideIcon]: isOpenInfo,
	});
	const closeIconClasses = classNames(style.icon, style.closeInfo, {
		[style.hideIcon]: !isOpenInfo,
	});
	const infoButtonClasses = classNames(btnClasses, {
		[style.activeBtnInfo]: isOpenInfo,
		[style.hidden]: !onClickInfo,
	});
	const editButtonClasses = classNames(btnClasses, { [style.hidden]: !URLWithIDToEdit && !onClickEdit });
	const archiveButtonClasses = classNames(btnClasses, { [style.hidden]: !onClickArchive });
	const restoreButtonClasses = classNames(btnClasses, { [style.hidden]: !onClickRestore });
	const deleteButtonClasses = classNames(btnClasses, { [style.hidden]: !onClickDelete });

	const infoButtonElement = URLWithIDToInfo ? (
		<Link className={btnClasses} to={URLWithIDToInfo}>
			<svg className={style.icon}>
				<use xlinkHref={`${breadcrumbs}#info`} />
			</svg>
		</Link>
	) : (
		<div className={infoButtonClasses} onClick={onClickInfo}>
			<svg className={infoIconClasses}>
				<use xlinkHref={`${breadcrumbs}#info`} />
			</svg>
			<svg className={closeIconClasses}>
				<use xlinkHref={`${breadcrumbs}#closeInfo`} />
			</svg>
		</div>
	);

	const editButtonElement = URLWithIDToEdit ? (
		<Link className={editButtonClasses} to={URLWithIDToEdit}>
			<svg className={style.icon}>
				<use xlinkHref={`${breadcrumbs}#pencil`} />
			</svg>
		</Link>
	) : (
		<div className={editButtonClasses} onClick={onClickEdit}>
			<svg className={style.icon}>
				<use xlinkHref={`${breadcrumbs}#pencil`} />
			</svg>
		</div>
	);

	return (
		<div className={style.container} data-test="DataController">
			{(URLWithIDToInfo || onClickInfo) && (
				<TooltipForElement title={t('additionalInfoTooltip')}>{infoButtonElement}</TooltipForElement>
			)}
			{!customEditButton ? (
				<TooltipForElement title={t('editTooltip')}>{editButtonElement}</TooltipForElement>
			) : (
				<CustomPop childrenContent={customEditButton}>
					<TooltipForElement title={t('editTooltip')}>
						<div className={btnClasses}>
							<svg className={style.icon}>
								<use xlinkHref={`${breadcrumbs}#pencil`} />
							</svg>
						</div>
					</TooltipForElement>
				</CustomPop>
			)}
			<TooltipForElement title={t('archiveTooltip')}>
				<div className={archiveButtonClasses} onClick={onClickArchive}>
					<svg className={style.icon}>
						<use xlinkHref={`${breadcrumbs}#archive`} />
					</svg>
				</div>
			</TooltipForElement>
			<TooltipForElement title={t('restoreTooltip')}>
				<div className={restoreButtonClasses} onClick={onClickRestore}>
					<svg className={style.icon}>
						<use xlinkHref={`${breadcrumbs}#restore`} />
					</svg>
				</div>
			</TooltipForElement>
			<TooltipForElement title={t('deleteTooltip')}>
				<div className={deleteButtonClasses} onClick={onClickDelete}>
					<svg className={style.icon}>
						<use xlinkHref={`${breadcrumbs}#bin`} />
					</svg>
				</div>
			</TooltipForElement>
		</div>
	);
};

export default DataController;
